import React, { useMemo, useState, useRef, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useSelector, useDispatch } from 'react-redux';
import { usePositionReorder } from '~/hooks/useDraggable';

// Actions
import {
  fetchTemplateCategories,
  sortTemplateCategory,
} from '~/redux/actions/admin-templates-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import DraggableList from '~/components/General/DraggableList';
import Loader from '~/components/General/Loader';
import OlcFormModal from '~/components/General/OlcFormModal';

// Mui Components
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, Divider } from '@mui/material';

// Styles
import './styles.scss';

const DragAndSortCategories = ({ openModal, handleCloseModal, pagination }) => {
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);

  const rows = useSelector((state) => state.adminTemplatesReducers.allTemplateCategories) || [];

  const [updatedList, updatePosition, updateOrder, setOrder] = usePositionReorder(rows);

  const memoizedList = useMemo(() => updatedList, [updatedList]);

  const dispatch = useDispatch();

  const reArrangeCategories = async () => {
    try {
      setLoading(true);
      const payload = {
        categories: updatedList.map((category, index) => ({
          id: +category?.id,
          sortOrder: index,
        })),
      };
      const response = await dispatch(sortTemplateCategory(payload, pagination));
      dispatch(success(response?.data?.message || response?.message));
      handleClose();
    } catch (error) {
      dispatch(failure(response?.data?.message || response?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setLoading(false);
    }
  };

  const getAllAdminCategories = async () => {
    setLoader(true);
    let payload = {
      pagination,
      getAll: true,
    };
    await dispatch(fetchTemplateCategories(payload));
    setLoader(false);
  };

  const handleClose = () => {
    setOpen(false);
    handleCloseModal();
  };

  useEffect(() => {
    setOrder(rows);
  }, [openModal, rows]);

  useEffect(() => {
    if (openModal) {
      getAllAdminCategories();
      setOpen(true);
    } else {
      setOrder([]);
    }
  }, [openModal]);

  return (
    <OlcFormModal
      open={open}
      onClose={handleClose}
      title={MESSAGES.ADMIN_DASHBOARD.CATEGORIES.REARRANGE.TITLE}
      className="olcAdminFormModal dragCategoryModalWrapper"
      dialogProps={{ maxWidth: false }}
      footer={
        <>
          <OlcButton variant="secondary" onClick={handleClose}>
            {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.REARRANGE.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={reArrangeCategories} disabled={loading || loader}>
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : (
              MESSAGES.ADMIN_DASHBOARD.CATEGORIES.REARRANGE.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      {loader ? (
        <Box className="dragCategoryLoader">
          <Loader />
        </Box>
      ) : rows?.length <= 0 ? (
        <Typography>No results meet your current search</Typography>
      ) : (
        <>
          <Divider sx={{ width: '100%' }} />
          <Box className="categoriesContainerWrapper">
            <ul className="categoriesContainer" ref={containerRef}>
              {memoizedList.map((item, index) => (
                <DraggableList
                  key={item.id || index}
                  index={index}
                  updateOrder={updateOrder}
                  updatePosition={updatePosition}
                  item={item}
                  containerRef={containerRef}
                />
              ))}
            </ul>
          </Box>
          <Divider sx={{ width: '100%' }} />
        </>
      )}
    </OlcFormModal>
  );
};

export default React.memo(DragAndSortCategories);
